// ═══════════════════════════════════════════════════════════════════════════
// TrueHealthAge — Food Guidance (added 2026-06-03 per Keith review)
//
// Turns a user's quiz answers into condition-aware "Foods You Should Eat /
// Foods to Avoid" guidance, plus a starter recipe for each detected condition.
// This is the "starting point for most conditions" Keith asked for.
//
// SCOPE + SAFETY
// --------------
// This is GENERAL WELLNESS information, not a prescription or medical
// nutrition therapy. Each condition carries a `caution` where a common
// drug/condition interaction matters (e.g. warfarin + leafy greens, kidney
// disease + potassium/protein). The results page renders a disclaimer
// alongside this content — do not present it as personalized medical advice.
//
// Detection reads the raw `answers` object from calculatePhase1Results
// (answers[qId].text). Conditions are additive: a user can match several.
// Order in CONDITION_GUIDANCE is the display priority (most clinically
// urgent first).
// ═══════════════════════════════════════════════════════════════════════════

// Each condition: { key, label, detect(answers) -> bool, intro, eat[], avoid[],
//   firstStep, caution, recipe }
const CONDITION_GUIDANCE = [
  {
    key: 'blood_sugar',
    label: 'Blood Sugar / Diabetes',
    detect: (a) => /pre-?diabet|type\s*2|type\s*1|\bt1\b|\bt2\b/i.test(a?.[5]?.text || ''),
    intro: 'Steadying your blood sugar is the single biggest lever here. The goal is simple: slow how fast sugar hits your bloodstream by pairing carbs with protein, fat, and fiber — and cutting the liquid sugar that spikes you fastest.',
    eat: [
      'Non-starchy vegetables — leafy greens, broccoli, peppers, zucchini (fill half your plate)',
      'Protein at every meal — eggs, fish, chicken, Greek yogurt, tofu, beans',
      'Healthy fats — olive oil, avocado, nuts, seeds (blunt the sugar spike)',
      'High-fiber carbs over refined — beans, lentils, steel-cut oats, berries',
      'Cinnamon, vinegar before carb-heavy meals (small but real glucose effect)',
    ],
    avoid: [
      'Sugar-sweetened drinks — soda, juice, sweet tea, energy drinks (the #1 spike)',
      'White bread, white rice, most breakfast cereals, pastries',
      'Candy, desserts, and "low-fat" products that replace fat with sugar',
      'Eating carbs alone — always pair with protein or fat',
    ],
    firstStep: 'This week: replace one sugary drink a day with water or unsweetened tea. That alone moves the needle faster than almost anything else.',
    caution: 'If you take insulin or sulfonylureas, big diet changes can drop your blood sugar too low — coordinate with your doctor before making large changes.',
    recipe: {
      name: 'Savory Protein Breakfast Bowl',
      why: 'Starts the day with protein + fat + fiber so you avoid the mid-morning crash and cravings.',
      ingredients: ['2 eggs', '1 cup spinach', '½ avocado', '½ cup black beans', 'Olive oil, salt, pepper, hot sauce'],
      steps: 'Sauté spinach in olive oil, push aside and scramble the eggs in the same pan. Warm the beans. Plate eggs + beans + spinach, top with sliced avocado and hot sauce. ~5 minutes.',
    },
  },
  {
    key: 'heart',
    label: 'Heart & Blood Pressure',
    detect: (a) =>
      /blood pressure|cholesterol|afib|heart failure|MI|stroke|stent|bypass|heart attack/i.test(a?.[23]?.text || '') ||
      /high/i.test(a?.[7]?.text || ''),
    intro: 'Your heart and arteries respond to food fast. The pattern with the strongest evidence is Mediterranean-style: lots of plants and olive oil, fish over red meat, and a hard cut on sodium and processed food. Blood pressure can start dropping within weeks.',
    eat: [
      'Fatty fish 2–3x/week — salmon, sardines, mackerel (omega-3s)',
      'Leafy greens and beets (natural nitrates relax blood vessels)',
      'Berries, oranges, bananas (potassium counters sodium)',
      'Olive oil, nuts, seeds as your main fats',
      'Oats, beans, lentils (soluble fiber lowers LDL cholesterol)',
    ],
    avoid: [
      'High-sodium processed foods — deli meats, canned soup, frozen dinners, chips',
      'Added salt at the table — taste before you reach for the shaker',
      'Fried foods and trans/partially-hydrogenated oils',
      'Sugar-sweetened drinks and excess alcohol (both raise blood pressure)',
    ],
    firstStep: 'This week: read the sodium line on one packaged food you eat daily and swap it for a lower-sodium version. Aim under 2,300 mg sodium/day total.',
    caution: 'If you take a blood thinner like warfarin, keep your leafy-green intake CONSISTENT (not necessarily low) and tell your doctor — vitamin K affects your dose. On BP meds, monitor for it dropping too low.',
    recipe: {
      name: 'Sheet-Pan Salmon & Greens',
      why: 'Omega-3s + potassium-rich vegetables + olive oil — a heart-protective plate with almost no sodium.',
      ingredients: ['1 salmon fillet', '2 cups broccoli or asparagus', 'Cherry tomatoes', 'Olive oil', 'Lemon, garlic, pepper (skip the salt)'],
      steps: 'Toss vegetables in olive oil, garlic, pepper on a sheet pan. Add salmon, squeeze lemon over it. Roast at 400°F for ~15 min. ~20 minutes total.',
    },
  },
  {
    key: 'weight',
    label: 'Weight & Metabolism',
    detect: (a) =>
      /overweight|obese/i.test(a?.[6]?.text || '') ||
      /apple|skinny-?fat/i.test(a?.[25]?.text || ''),
    intro: 'The fat that ages you most is visceral fat — the deep belly fat that pumps out inflammation. The fastest way to lose it isn\'t cutting calories alone; it\'s raising protein and fiber so you stay full on fewer calories, and cutting the refined carbs that drive belly-fat storage.',
    eat: [
      'Protein first at every meal — keeps you full and protects muscle while losing fat',
      'High-volume, low-calorie vegetables — fill up without the calories',
      'Fiber-rich foods — beans, lentils, berries, oats (satiety + gut health)',
      'Water before meals; whole fruit instead of dessert',
    ],
    avoid: [
      'Liquid calories — soda, juice, sweetened coffee, alcohol',
      'Refined carbs — white bread, chips, crackers, pastries',
      'Ultra-processed snacks engineered to override fullness',
      'Eating from the bag/container — portion onto a plate',
    ],
    firstStep: 'This week: add a palm-sized portion of protein to your breakfast. People who do this eat ~100–200 fewer calories the rest of the day without trying.',
    caution: 'Rapid or extreme calorie restriction backfires (muscle loss, rebound). Aim for steady, sustainable change. If you take medication for diabetes or BP, dosing may need adjusting as you lose weight.',
    recipe: {
      name: 'Big Protein Salad',
      why: 'High protein + high fiber + high volume = full on far fewer calories.',
      ingredients: ['2 cups mixed greens', '1 cup grilled chicken or chickpeas', 'Cucumber, tomato, peppers', '¼ avocado', 'Olive oil + vinegar'],
      steps: 'Pile greens and chopped veg in a big bowl, add protein and avocado, dress with olive oil and vinegar. Eat slowly. ~10 minutes.',
    },
  },
  {
    key: 'inflammation',
    label: 'Inflammation & Cellular Aging',
    detect: (a) =>
      /50% of meals|80%\+/i.test(a?.[16]?.text || '') ||
      /daily|few times a week/i.test(a?.[17]?.text || '') ||
      /remission|active|recent|metasta/i.test(a?.[24]?.text || ''),
    intro: 'Chronic low-grade inflammation is the common thread under most age-related disease. An anti-inflammatory plate — colorful plants, omega-3s, and far fewer ultra-processed foods — gives your repair systems room to work.',
    eat: [
      'Colorful plants — the more colors, the more protective polyphenols',
      'Fatty fish, walnuts, flax, chia (omega-3s lower inflammation)',
      'Extra-virgin olive oil (its own anti-inflammatory compounds)',
      'Berries, leafy greens, cruciferous veg (broccoli, cauliflower, kale)',
      'Green tea, herbs and spices — turmeric, ginger, garlic',
    ],
    avoid: [
      'Ultra-processed packaged foods and fast food',
      'Industrial seed oils heated for frying (soybean, corn, cottonseed)',
      'Processed and charred/burnt red meats',
      'Added sugar and refined flour',
    ],
    firstStep: 'This week: add one serving of a deeply colored vegetable or a handful of berries to a meal each day. Crowd the good in before you fight to take the bad out.',
    caution: 'If you are in cancer treatment or remission, run any major diet change past your oncology team — some foods and supplements interact with treatment.',
    recipe: {
      name: 'Rainbow Stir-Fry',
      why: 'A spread of colors delivers a wide range of anti-inflammatory polyphenols in one easy pan.',
      ingredients: ['Broccoli, red pepper, carrot, snap peas, red cabbage', 'Tofu, shrimp, or chicken', 'Olive or avocado oil', 'Garlic, ginger, low-sodium soy sauce'],
      steps: 'Stir-fry protein in oil, set aside. Stir-fry chopped veg until crisp-tender, add garlic + ginger. Return protein, splash of low-sodium soy. Serve over brown rice or as-is. ~15 minutes.',
    },
  },
]

/**
 * Detect which conditions apply and return tailored food guidance.
 * @param {object} answers — raw answers object from calculatePhase1Results.
 * @returns {Array} matched condition guidance objects (display order).
 */
export function getFoodGuidance(answers) {
  if (!answers) return []
  return CONDITION_GUIDANCE.filter(c => {
    try {
      return c.detect(answers)
    } catch {
      return false
    }
  })
}
